import { Dispatch, SetStateAction } from "react";
import { capitalizeFirstLetters } from "../../../utils/stringUtils";
import CustomLabel from "../../common/CustomLabel";
import { DateType, FilterCriteria } from "../../../types";
import CustomToggle from "../../common/CustomToggle";
import CustomAccordion from "../../common/CustomAccordion";

const ReportDateFilter = ({
  filterCriteria,
  setFilterCriteria,
}: {
  filterCriteria: FilterCriteria;
  setFilterCriteria: Dispatch<SetStateAction<FilterCriteria>>;
}) => {
  const handleDate = (
    key: "type" | "date",
    value: DateType["type"] | Partial<Omit<DateType, "type">>
  ) => {
    setFilterCriteria((prev) => ({
      ...prev,
      date:
        key === "type"
          ? { ...prev.date, type: value as DateType["type"] }
          : { ...prev.date, ...(value as Partial<Omit<DateType, "type">>) },
    }));
  };

  return (
    <CustomAccordion label="Date" defaultOpen={true}>
      <div className="flex flex-col items-center gap-4">
        <CustomToggle
          className="w-[300px]"
          value={filterCriteria.date.mode}
          onToggle={() =>
            setFilterCriteria((prev) => ({
              ...prev,
              date: {
                ...prev.date,
                mode:
                  prev.date.mode === "incidentDate"
                    ? "reportedDate"
                    : "incidentDate",
              },
            }))
          }
          optionOne={{ label: "Incident Date", value: "incidentDate" }}
          optionTwo={{ label: "Reported Date", value: "reportedDate" }}
        />

        <p className="mb-1 font-extrabold text-base leading-5">Select Range</p>

        <CustomLabel
          labelText="Select Range"
          labelTextClass="px-4"
          labelWrapClass="!gap-1"
        >
          <div className="flex items-center justify-between w-full gap-2">
            {["all", "specific", "range"].map((btnMode) => (
              <button
                key={btnMode}
                onClick={() =>
                  handleDate("type", btnMode as "all" | "specific" | "range")
                }
                className={`cursor-pointer rounded-[50px] px-8 py-2 ${
                  filterCriteria.date.type === btnMode
                    ? "bg-gray-7 text-white"
                    : "bg-gray-2 text-gray-4"
                }`}
              >
                {capitalizeFirstLetters(btnMode)}
              </button>
            ))}
          </div>
        </CustomLabel>

        <div className="flex justify-center w-full">
          {filterCriteria.date.type === "all" && (
            <div className="bg-gray-2 w-[150px] h-[46px] rounded-[25.16px] py-[11px] px-3" />
          )}

          {filterCriteria.date.type === "specific" && (
            <input
              type="date"
              className="border-[1.5px] border-gray-7 rounded-[50px] py-[11px] px-3"
              value={filterCriteria.date.startDate || ""}
              onChange={(e) =>
                handleDate("date", {
                  startDate: e.target.value,
                  endDate: e.target.value,
                })
              }
            />
          )}

          {filterCriteria.date.type === "range" && (
            <div className="flex justify-between items-center w-full">
              <input
                type="date"
                value={filterCriteria.date.startDate || ""}
                onChange={(e) =>
                  handleDate("date", { startDate: e.target.value })
                }
                className="border-[1.5px] border-gray-7 rounded-[50px] py-[11px] px-2"
              />
              <p className="font-normal text-sm leading-4 text-gray-7">TO</p>
              <input
                type="date"
                value={filterCriteria.date.endDate || ""}
                onChange={(e) =>
                  handleDate("date", { endDate: e.target.value })
                }
                className="border-[1.5px] border-gray-7 rounded-[50px] py-[11px] px-2"
              />
            </div>
          )}
        </div>
      </div>
    </CustomAccordion>
  );
};

export default ReportDateFilter;
